import React, { useState } from 'react';
import { TextField, Button, Box, Snackbar, Alert } from '@mui/material';
import { AuthAPI } from '../apis/Auth';
import { useTranslation } from 'react-i18next';
import { ROUTES_NS } from './i18n';
import { PageTitle } from '../components/Page/PageTitle';

const PasswordReset: React.FC = () => {
  const { t } = useTranslation(ROUTES_NS);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const validateForm = (): boolean => {
    if (oldPassword === newPassword) {
      setError('New password should not be the same as the old password.');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation password do not match.');
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      setSnackbarOpen(true);
      return;
    }

    try {
      await AuthAPI.Reset({ newPassword, oldPassword });

      setSuccess(true);
      setSnackbarOpen(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setError('Failed to reset password. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    setSuccess(false);
    setError('');
  };

  return (
    <main className="app-content">
      <Box
        sx={{
          height: '95vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <PageTitle>{t('passwordReset')}</PageTitle>
        <Box
          sx={{
            minWidth: '300px',
            maxWidth: '500px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: '0',
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 2,
            }}
          >
            <TextField
              label="Old Password"
              type="password"
              fullWidth
              required
              value={oldPassword}
              variant="standard"
              onChange={(e) => setOldPassword(e.target.value)}
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
              required
              value={newPassword}
              variant="standard"
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              required
              value={confirmPassword}
              variant="standard"
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Reset
            </Button>
          </Box>
        </Box>
        {error && (
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        )}
        {success && (
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
              Password reset successfully.
            </Alert>
          </Snackbar>
        )}
      </Box>
    </main>
  );
};

export default PasswordReset;
